import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Icon } from "./ui-kit";
import { cn } from "../lib/utils";
import api from "../lib/api";

const NAV = {
  student: [
    { to: "/app", label: "Home", icon: "House" },
    { to: "/learn", label: "Learn", icon: "BookOpen" },
    { to: "/exam-radar", label: "Exam Radar", icon: "Radar" },
    { to: "/videos", label: "Videos", icon: "PlaySquare" },
    { to: "/ask-ai", label: "Ask AI", icon: "Sparkles" },
    { to: "/progress", label: "Progress", icon: "TrendingUp" },
  ],
  teacher: [
    { to: "/teacher", label: "Dashboard", icon: "LayoutDashboard" },
    { to: "/teacher/students", label: "Students", icon: "Users" },
    { to: "/teacher/studio", label: "AI Studio", icon: "Sparkles" },
    { to: "/content", label: "Content Studio", icon: "FolderTree" },
    { to: "/teacher/question-bank", label: "Question Bank", icon: "Database" },
    { to: "/teacher/curriculum", label: "Curriculum AI", icon: "Wand2" },
  ],
  school_admin: [
    { to: "/school", label: "Dashboard", icon: "LayoutDashboard" },
    { to: "/teacher/students", label: "Students", icon: "Users" },
    { to: "/content", label: "Content Studio", icon: "FolderTree" },
    { to: "/teacher/curriculum", label: "Curriculum AI", icon: "Wand2" },
  ],
  super_admin: [
    { to: "/admin", label: "Dashboard", icon: "LayoutDashboard" },
    { to: "/content", label: "Content Studio", icon: "FolderTree" },
    { to: "/teacher/curriculum", label: "AI Content Factory", icon: "Wand2" },
    { to: "/teacher/students", label: "Users", icon: "Users" },
  ],
  parent: [
    { to: "/parent", label: "Overview", icon: "House" },
  ],
  independent_educator: [
    { to: "/teacher", label: "Dashboard", icon: "LayoutDashboard" },
    { to: "/teacher/studio", label: "AI Studio", icon: "Sparkles" },
  ],
};

const ROLE_LABEL = {
  student: "Student", teacher: "Teacher", school_admin: "School Admin",
  super_admin: "Super Admin", parent: "Parent", independent_educator: "Educator",
};

function Brand({ small }) {
  return (
    <img src="/logo.png" alt="Master in Minutes"
      className={cn("object-contain select-none", small ? "h-9" : "h-12")} />
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = NAV[user?.role] || [];
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const isStudent = user?.role === "student";

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, [location.pathname]);

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("");

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5"><Brand /></div>
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === "/app" || item.to === "/teacher"}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all",
              isActive ? "bg-mim-soft text-mim-blue" : "text-mim-muted hover:bg-slate-50 hover:text-mim-navy"
            )}>
            <Icon name={item.icon} className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-mim-line">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-mim-purple/15 text-mim-purple flex items-center justify-center text-sm font-bold shrink-0">{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-mim-navy truncate">{user?.name}</div>
            <div className="text-[11px] text-mim-muted">{ROLE_LABEL[user?.role]}</div>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="p-2 rounded-lg text-mim-muted hover:bg-mim-coral/10 hover:text-mim-coral transition-colors">
            <Icon name="LogOut" className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-mim-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-mim-line z-40">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-mim-navy/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-white animate-pop">{SidebarInner}</div>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="bg-white/90 backdrop-blur-md border-b border-mim-line sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 sm:px-6 h-16">
            <div className="flex items-center gap-3">
              <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
                <Icon name="Menu" className="w-5 h-5 text-mim-navy" />
              </button>
              <div className="lg:hidden"><Brand small /></div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <NavLink to="/notifications" className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notif-btn">
                <Icon name="Bell" className="w-5 h-5 text-mim-navy" />
                {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-mim-coral text-white text-[9px] font-bold flex items-center justify-center">{unread}</span>}
              </NavLink>
              {isStudent && (
                <NavLink to="/ask-ai" className="hidden sm:inline-flex items-center gap-1.5 bg-mim-purple text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-mim-purple/90 transition-colors" data-testid="topbar-askai">
                  <Icon name="Sparkles" className="w-4 h-4" /> Ask AI
                </NavLink>
              )}
            </div>
          </div>
        </header>

        <main className="pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Student bottom nav (mobile) */}
      {isStudent && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur-md border-t border-mim-line z-40 px-2 pb-safe">
          <div className="flex items-center justify-around h-16">
            {nav.slice(0, 5).map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/app"}
                data-testid={`bottomnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 py-1 rounded-xl transition-colors",
                  isActive ? "text-mim-blue" : "text-mim-muted"
                )}>
                <Icon name={item.icon} className="w-5 h-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
